import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Phone, X } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { PUBLIC_NAV_ITEMS } from './publicNav';
import { useRestaurantSettings } from '@/features/restaurant/useRestaurantSettings';
import { useScrollSperre } from '@/components/ui/useScrollSperre';

/**
 * Public-Header — 1:1-Umsetzung von Die Sizilianer.html `<nav>` + `<div class="nav-rule">`.
 *
 * Klassen-Namen kommen aus src/styles/sizilianer-design.css.
 * Mobile fällt auf eine kompaktere Variante zurück (Burger-Drawer).
 * Nav-Items: publicNav.ts (SSOT) — 6 Kernpunkte, Home via Logo (02.N1).
 */

export function PublicHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { data: settings } = useRestaurantSettings();

  // 05.M1 / 14.ALL1: Telefon above-the-fold — Nummer aus dem Backoffice
  const phone = settings?.phone ?? '02362 23279';
  const phoneHref = `tel:${phone.replace(/\s/g, '').replace(/^0/, '+49')}`;

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  /**
   * 🚨 E-45 (14.09.2026): DIE EIGENBAU-SPERRE IST HIER ERSETZT, NICHT ERGÄNZT.
   *
   * Bis heute stand an dieser Stelle ein eigener Effekt, der `body.style
   * .overflow` auf `'hidden'` setzte. Er war die letzte Stelle im Repo, die
   * das selbst tat — und dreifach unterlegen:
   *
   *  1. **Er hält auf dem iPhone nicht.** Safari lässt sich mit dem Finger
   *     über ein `overflow: hidden` am body hinwegschieben. Genau das ist der
   *     Grund, aus dem `useScrollSperre` den body aus dem Fluss nimmt.
   *  2. **Er setzte beim Aufräumen `''` statt des vorherigen Werts.** Stand
   *     dort vorher etwas anderes, war es danach weg.
   *  3. **Er zählte nicht mit.** Zwei Schreiber auf demselben Feld löschen
   *     sich gegenseitig aus — dieselbe Lehre, die im Kopf des Hakens zum
   *     `paddingBottom` steht.
   *
   * Der Effekt darüber (Schließen beim Seitenwechsel) bleibt unberührt.
   */
  useScrollSperre(mobileOpen);

  return (
    <>
      {/* Desktop-Nav (>= 1024px) — exakt aus Design.
          <header>-Landmark (WCAG 1.3.1): .nav/.nav-rule bringen ihr
          display:grid aus der CSS mit, der Wrapper steuert nur Sichtbarkeit. */}
      <header className="hidden lg:block">
      <nav className="nav">
        <Link to="/" className="logo" style={{ textDecoration: 'none' }}>
          <div className="logo-mark">
            <img src="/img/Botanische%20Ruhe%20und%20Harmonie%2096.webp"
            srcSet="/img/Botanische%20Ruhe%20und%20Harmonie%2096.webp 96w, /img/Botanische%20Ruhe%20und%20Harmonie%20240.webp 240w"
            sizes="48px"
            width={96} height={96} alt="" />
          </div>
          <div className="logo-top">DIE SIZILIANER</div>
          <div className="logo-sub">Sizilianische Spezialitäten</div>
        </Link>

        <div className="nav-links">
          {PUBLIC_NAV_ITEMS.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === '/'}
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              {t(`nav.${item.tKey}`)}
            </NavLink>
          ))}
        </div>

        {/* Das aria-label trägt die Nummer unabhängig davon, ob der Text
            sichtbar ist: unter 1500px weicht `.nav-tel-nr` (siehe
            sizilianer-design.css), weil der Nav dort die Breite für alle
            fünf Spalten fehlt. Das Hörer-Symbol bleibt klickbar, damit
            05.M1/14.ALL1 „Telefon above-the-fold" erfüllt bleibt. */}
        <a className="nav-tel" href={phoneHref} aria-label={`Anrufen: ${phone}`}>
          <span className="ic" aria-hidden="true">
            <Phone className="size-4" />
          </span>
          <span className="nav-tel-nr">{phone}</span>
        </a>

        <Link className="btn-reserve" to="/reservieren">
          {t('nav.reservation')}
        </Link>

        <div className="lang">
          <LanguageSwitcher />
        </div>
      </nav>

      {/* Tricolore-Rule unter Desktop-Nav */}
      <div className="nav-rule">
        <div className="r green"></div>
        <div className="diamond"></div>
        <div className="r red"></div>
      </div>
      </header>

      {/* Mobile-Nav (< 1024px) — kompakter Logo-Bar mit Burger */}
      <header className="lg:hidden sticky top-0 z-40 bg-paper/95 backdrop-blur-md border-b border-rule-soft">
        <div className="flex items-center justify-between gap-4 px-5 py-3">
          <Link to="/" className="flex items-center gap-3">
            <div
              className="size-12 rounded-full border-[1.5px] border-ink-2 overflow-hidden flex items-center justify-center"
              style={{
                backgroundImage:
                  'linear-gradient(var(--color-ink-2), var(--color-ink-2)), linear-gradient(var(--color-ink-2), var(--color-ink-2))',
                backgroundSize: '70% 1px, 70% 1px',
                backgroundPosition: '50% 9px, 50% calc(100% - 9px)',
                backgroundRepeat: 'no-repeat',
              }}
            >
              <img
                src="/img/Botanische%20Ruhe%20und%20Harmonie%2096.webp"
            srcSet="/img/Botanische%20Ruhe%20und%20Harmonie%2096.webp 96w, /img/Botanische%20Ruhe%20und%20Harmonie%20240.webp 240w"
            sizes="48px"
            width={96} height={96}
                alt=""
                className="size-[62%] object-contain"
                style={{ filter: 'contrast(1.1)' }}
              />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-serif text-[12px] tracking-[0.22em] font-semibold text-ink-2 leading-none">
                DIE SIZILIANER
              </span>
              <span className="font-script text-sm text-ink-soft leading-none mt-0.5">
                Sizilianische Spezialitäten
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-1">
            <LanguageSwitcher compact />
            {/* 09.T1: ≥44px Touch-Target; 11.NAV1: sichtbares Wort-Label „Menü" */}
            <button
              type="button"
              className="inline-flex items-center gap-1.5 p-2.5 -mr-2.5 min-h-11 text-ink-2 hover:text-gold-2 transition-colors"
              onClick={() => setMobileOpen(true)}
              aria-label="Menü öffnen"
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
            >
              <Menu className="size-6" aria-hidden="true" />
              <span className="font-sans text-2xs uppercase tracking-[0.18em] font-semibold">
                Menü
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile-Drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          id="mobile-nav"
        >
          <button
            type="button"
            className="absolute inset-0 bg-ink-2/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-label="Menü schließen"
          />
          <div className="absolute right-0 top-0 h-full w-[85vw] max-w-sm bg-paper shadow-lg flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-rule">
              <span className="font-serif text-lg tracking-[0.18em] text-ink-2">
                MENÜ
              </span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="p-2.5 -mr-2.5 text-ink-2 hover:text-gold-2"
                aria-label="Menü schließen"
              >
                <X className="size-6" aria-hidden="true" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-6">
              <ul className="flex flex-col gap-1">
                {PUBLIC_NAV_ITEMS.map((item) => (
                  <li key={item.href}>
                    <NavLink
                      to={item.href}
                      end={item.href === '/'}
                      className={({ isActive }) =>
                        `block py-3 px-2 font-sans text-sm uppercase tracking-[0.22em] border-b border-rule-soft ${isActive ? 'text-it-green' : 'text-ink-2'}`
                      }
                    >
                      {t(`nav.${item.tKey}`)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="p-6 border-t border-rule">
              <Link
                to="/reservieren"
                onClick={() => setMobileOpen(false)}
                className="btn-reserve block text-center"
              >
                {t('nav.reservation')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
